import { OverviewCardWidgetType, WidgetType } from "@/constants/dashboard.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { isModuleDisabled } from "@/_helpers/subscription";
import { type TicketsScope } from "@/_store/roles.module.ts";
import type { DataValue } from "@/types.ts";
import type { Component } from "vue";
import type {
  CloudSecurityWidgetData,
  EdrWidgetData,
  EmailSecurityWidgetData,
  EndpointDataGovernanceWidgetData,
  EndpointSecurityWidgetData,
  OverviewCardWidgetData,
  SecurityAwarenessWidgetData,
  UserDataGovernanceWidgetData,
} from "@/_store/dashboard/dashboard.module.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";
import type { BackupAndRestoreWidgetData } from "@/constants/cloud-backup.ts";

/** Maps widget types to their specific data shapes. */
export interface WidgetDataMap {
  [WidgetType.CLOUD_SECURITY]: CloudSecurityWidgetData;
  [WidgetType.EMAIL_SECURITY]: EmailSecurityWidgetData;
  [WidgetType.ENDPOINT_SECURITY]: EndpointSecurityWidgetData;
  [WidgetType.USER_DATA_GOVERNANCE]: UserDataGovernanceWidgetData;
  [WidgetType.ENDPOINT_DATA_GOVERNANCE]: EndpointDataGovernanceWidgetData;
  [WidgetType.EDR]: EdrWidgetData;
  [WidgetType.SECURITY_AWARENESS_TRAINING]: SecurityAwarenessWidgetData;
  [WidgetType.BACKUP_AND_RESTORE]: BackupAndRestoreWidgetData;
}

/** Defines the possible states a widget can be in. */
export enum WidgetState {
  HAS_OPEN_TICKETS = "hasOpenTickets",
  SETUP_REQUIRED = "setupRequired",
  ALL_CAUGHT_UP = "allCaughtUp",
}

/** Contains calculated metadata about a widget's status. */
export interface WidgetMetadata {
  disabled: boolean;
  restricted: boolean;
  state: WidgetState;
}

/** The base interface containing properties common to all widget configurations. */
export interface BaseWidgetConfiguration<Data extends object, Type extends string> {
  id: string;
  type: Type;
  data: DataValue<Data>;
  getData: () => Promise<void>;
}

/** Configuration for a standard, component-based widget. */
export interface WidgetConfiguration<T extends WidgetType> extends BaseWidgetConfiguration<
  WidgetDataMap[T],
  T
> {
  component: Component;
  metadata: WidgetMetadata;
}

/** Configuration for an Overview Card widget. */
export interface OverviewCardWidgetConfiguration extends BaseWidgetConfiguration<
  OverviewCardWidgetData,
  OverviewCardWidgetType
> {
  metadata: {
    changeType?: "increase" | "decrease" | "static";
    restricted?: boolean;
  };
}

/**
 * Creates a configuration object for an Overview Card widget.
 * @param type - The specific type of the overview card widget.
 * @param data - The reactive DataValue object from the store.
 * @param getData - The function that fetches the data for this widget.
 * @param changeType - Defines whether an 'increase' or 'decrease' is positive.
 * @param restricted - Flag to indicate if access to the widget is restricted.
 */
export function createOverviewCardWidget(
  type: OverviewCardWidgetType,
  data: DataValue<OverviewCardWidgetData>,
  getData: () => Promise<void>,
  changeType?: "increase" | "decrease" | "static",
  restricted?: boolean
): OverviewCardWidgetConfiguration {
  return {
    id: type,
    type,
    data,
    getData,
    metadata: {
      changeType,
      restricted,
    },
  };
}

/**
 * Creates a configuration object for a standard, component-based widget.
 * @returns A complete WidgetConfiguration object with its metadata calculated.
 */
export function createWidget<T extends WidgetType>(
  type: T,
  component: Component,
  data: DataValue<WidgetDataMap[T]>,
  getData: () => Promise<void>
): WidgetConfiguration<T> {
  return {
    id: type,
    type,
    component,
    data,
    getData,
    metadata: getMetaData(type, data),
  };
}

/** Maps each WidgetType to its corresponding subscription module. */
export const WIDGET_MODULE_MAP: Record<WidgetType, SubscriptionModule> = {
  [WidgetType.CLOUD_SECURITY]: SubscriptionModule.CLOUD_SECURITY,
  [WidgetType.EMAIL_SECURITY]: SubscriptionModule.EMAIL_SECURITY,
  [WidgetType.ENDPOINT_SECURITY]: SubscriptionModule.ENDPOINT_SECURITY,
  [WidgetType.USER_DATA_GOVERNANCE]: SubscriptionModule.USER_DATA_GOVERNANCE,
  [WidgetType.ENDPOINT_DATA_GOVERNANCE]: SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
  [WidgetType.EDR]: SubscriptionModule.EDR,
  [WidgetType.SECURITY_AWARENESS_TRAINING]: SubscriptionModule.SECURITY_AWARENESS_TRAINING,
  [WidgetType.BACKUP_AND_RESTORE]: SubscriptionModule.BACKUP_AND_RESTORE,
};

/**
 * A reverse map of {@link WIDGET_MODULE_MAP} for efficient lookups.
 * This maps each `SubscriptionModule` back to its corresponding `WidgetType`.
 *
 * @type {Record<SubscriptionModule, WidgetType>}
 * @see WIDGET_MODULE_MAP
 */
export function findWidgetByModule(moduleToFind: SubscriptionModule): WidgetType | undefined {
  // Find the first key (widget) whose value matches the module.
  return (Object.keys(WIDGET_MODULE_MAP) as WidgetType[]).find(
    (widget) => WIDGET_MODULE_MAP[widget] === moduleToFind
  );
}

/** Defines the signature for a function that calculates a widget's state from its data. */
type StateStrategy<T extends WidgetType> = (data: WidgetDataMap[T]) => WidgetState;

const getConnectedServicesWidgetState: StateStrategy<
  WidgetType.CLOUD_SECURITY | WidgetType.USER_DATA_GOVERNANCE
> = (data) => {
  if (!data.hasConnectedServices) return WidgetState.SETUP_REQUIRED;
  if (data.activeVulnerabilities.length > 0) return WidgetState.HAS_OPEN_TICKETS;
  return WidgetState.ALL_CAUGHT_UP;
};

const getProtectedDevicesWidgetState: StateStrategy<
  WidgetType.ENDPOINT_SECURITY | WidgetType.ENDPOINT_DATA_GOVERNANCE | WidgetType.EDR
> = (data) => {
  if (data.protectedDevicesCount === 0) return WidgetState.SETUP_REQUIRED;
  if (data.activeVulnerabilities.length > 0) return WidgetState.HAS_OPEN_TICKETS;
  return WidgetState.ALL_CAUGHT_UP;
};

const getEmailSecurityWidgetState: StateStrategy<WidgetType.EMAIL_SECURITY> = (data) => {
  const isEmailServicesConnected = data.hasConnectedServices || data.isProxyActive;
  if (!isEmailServicesConnected) return WidgetState.SETUP_REQUIRED;
  if (data.activeVulnerabilities.length > 0) return WidgetState.HAS_OPEN_TICKETS;
  return WidgetState.ALL_CAUGHT_UP;
};

const getSecurityAwarenessTrainingWidgetState: StateStrategy<
  WidgetType.SECURITY_AWARENESS_TRAINING
> = (data) => {
  return data.activated ? WidgetState.ALL_CAUGHT_UP : WidgetState.SETUP_REQUIRED;
};

// The Cloud Backup widget renders in its own dedicated section and isn't grouped
// by state, so it has no meaningful state — always report ALL_CAUGHT_UP.
const getBackupAndRestoreWidgetState: StateStrategy<WidgetType.BACKUP_AND_RESTORE> = () =>
  WidgetState.ALL_CAUGHT_UP;

/** A map of widget types to their corresponding state calculation functions. */
const WIDGET_STATE_STRATEGIES: { [K in WidgetType]?: StateStrategy<K> } = {
  [WidgetType.CLOUD_SECURITY]: getConnectedServicesWidgetState,
  [WidgetType.USER_DATA_GOVERNANCE]: getConnectedServicesWidgetState,
  [WidgetType.ENDPOINT_SECURITY]: getProtectedDevicesWidgetState,
  [WidgetType.ENDPOINT_DATA_GOVERNANCE]: getProtectedDevicesWidgetState,
  [WidgetType.EMAIL_SECURITY]: getEmailSecurityWidgetState,
  [WidgetType.EDR]: getProtectedDevicesWidgetState,
  [WidgetType.SECURITY_AWARENESS_TRAINING]: getSecurityAwarenessTrainingWidgetState,
  [WidgetType.BACKUP_AND_RESTORE]: getBackupAndRestoreWidgetState,
};

/**
 * Calculates and returns the complete metadata for a given widget.
 * This is the central engine that determines a widget's disabled, restricted, and state properties.
 */
function getMetaData<T extends WidgetType>(
  type: T,
  data: DataValue<WidgetDataMap[T]>
): WidgetMetadata {
  const module = WIDGET_MODULE_MAP[type];
  const disabled = isModuleDisabled(module);
  const widgetData = data.data;

  // Handle the special permission scope for the Security Awareness and Cloud
  // Backup widgets, which are gated by Protection access rather than Tickets.
  const restrictionScope =
    type === WidgetType.SECURITY_AWARENESS_TRAINING || type === WidgetType.BACKUP_AND_RESTORE
      ? RolePermissionsScope.PROTECTION
      : RolePermissionsScope.TICKETS;
  const restricted = isAccessRestricted(restrictionScope, module as keyof TicketsScope);

  // Return early for disabled modules or if data is not yet available
  if (disabled || !widgetData) {
    return { state: WidgetState.ALL_CAUGHT_UP, restricted, disabled };
  }

  // Look up the state calculation strategy for the widget type
  const strategy = WIDGET_STATE_STRATEGIES[type];

  // 💡 Throws an error if the strategy is missing, ensuring all widgets are handled.
  if (!strategy) {
    throw new Error(`No state strategy found for widget type: "${type}"`);
  }

  const state = strategy(widgetData);

  return { disabled, restricted, state };
}
